/**
 * Медиа-карточка §61: главный объект — превью, служебная информация вторична.
 * Действия — по hover (§62), а не постоянно на карточке.
 */
import { useState } from 'react';
import { Download, Trash2 } from 'lucide-react';
import type { AssetView } from '@alterium/shared';
import { assetsApi } from '../../api/assets';
import { useTranslation } from 'react-i18next';
import { i18n } from '../../i18n';

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return i18n.t('media.size.b', { value: bytes });
  if (bytes < 1024 * 1024) return i18n.t('media.size.kb', { value: Math.round(bytes / 1024) });
  return i18n.t('media.size.mb', { value: (bytes / 1024 / 1024).toFixed(1) });
};

const formatDuration = (seconds: number): string => {
  const total = Math.round(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

export function MediaCard({
  asset,
  onDelete,
}: {
  asset: AssetView;
  onDelete: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const preview = asset.posterUrl ?? (asset.kind === 'image' ? asset.url : null);

  const download = async () => {
    setBusy(true);
    try {
      const { url } = await assetsApi.downloadUrl(asset.id);
      window.open(url, '_blank', 'noopener');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="group relative overflow-hidden rounded-card border border-line bg-surface">
      <div className="relative aspect-[4/3] bg-surface-secondary">
        {preview ? (
          <img
            src={preview}
            alt={asset.originalName ?? ''}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-meta text-content-muted">
            {asset.mime.split('/')[1]?.toUpperCase() ?? t('media.file')}
          </div>
        )}

        {asset.durationSec ? (
          <span className="absolute bottom-2 right-2 rounded bg-page/80 px-1.5 py-0.5 text-[11px] text-content">
            {formatDuration(asset.durationSec)}
          </span>
        ) : null}

        {/* §62: действия появляются по hover, не занимают карточку постоянно */}
        <div className="absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity duration-fast group-hover:opacity-100">
          <button
            type="button"
            onClick={() => void download()}
            disabled={busy}
            title={t('render.download')}
            aria-label={t('render.download')}
            className="rounded-control bg-page/80 p-1.5 text-content-secondary transition-colors duration-fast hover:text-content"
          >
            <Download className="h-4 w-4" strokeWidth={1.75} />
          </button>
          <button
            type="button"
            onClick={() => onDelete(asset.id)}
            title={t('common.delete')}
            aria-label={t('common.delete')}
            className="rounded-control bg-page/80 p-1.5 text-content-secondary transition-colors duration-fast hover:text-error"
          >
            <Trash2 className="h-4 w-4" strokeWidth={1.75} />
          </button>
        </div>
      </div>

      <div className="px-3 py-2.5">
        <p className="truncate text-meta text-content">{asset.originalName ?? t('media.noName')}</p>
        <p className="text-[11px] text-content-muted">
          {asset.width && asset.height ? `${asset.width}×${asset.height} · ` : ''}
          {formatBytes(asset.bytes)}
        </p>
      </div>
    </div>
  );
}
